import { useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, Download, GitBranch, Scale, Smartphone, Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { ModelReviews } from "./ModelReviews";
import { evaluate, recommendedQuant, verdictMeta } from "../lib/compute";
import { useHardware } from "../context/HardwareContext";
import { VerdictBadge } from "./VerdictBadge";
import { InstallCommands } from "./InstallCommands";
import { FavoriteButton, catalogFavorite } from "./FavoriteButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";

const USECASE_COLORS = {
  chat: "text-sky-400 border-sky-900 bg-sky-950/40",
  code: "text-violet-400 border-violet-900 bg-violet-950/40",
  reasoning: "text-fuchsia-400 border-fuchsia-900 bg-fuchsia-950/40",
  vision: "text-emerald-400 border-emerald-900 bg-emerald-950/40",
  multilingual: "text-amber-400 border-amber-900 bg-amber-950/40",
  edge: "text-slate-400 border-slate-800 bg-slate-900/40",
  mobile: "text-teal-300 border-teal-800 bg-teal-950/40",
};

const BAR_TONE = { emerald: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };

const useReviewSummary = () =>
  useQuery({ queryKey: ["review-summary"], queryFn: async () => (await api.get("/reviews/summary")).data, staleTime: 60 * 1000 });

export function ModelCard({ model, quantizations }) {
  const { hardware, compare, toggleCompare } = useHardware();
  const rec = recommendedQuant(model, hardware, quantizations);
  const [quantId, setQuantId] = useState(rec.id);
  const quant = quantizations.find((q) => q.id === quantId) || rec;
  const ev = evaluate(model, quant, hardware);
  const tone = verdictMeta(ev.verdict).tone;
  const fill = Math.min(100, Math.round((ev.requiredGB / Math.max(ev.target, 0.1)) * 100));
  const checked = compare.includes(model.id);
  const { data: summaries } = useReviewSummary();
  const summary = summaries?.[model.id];

  return (
    <div
      data-testid="model-card"
      className="group flex flex-col rounded-xl border border-[#1E2638] bg-[#0F141C] p-5 transition-all duration-200 hover:border-primary/70 hover:shadow-[0_0_0_1px_rgba(6,182,212,0.25)]"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-1.5 font-head text-base font-bold leading-tight">
            {model.name}
            {model.use_cases.includes("mobile") && (
              <Smartphone className="h-3.5 w-3.5 text-teal-300" data-testid="mobile-indicator" title="Installable sur iPhone" />
            )}
          </h3>
          <p className="mt-0.5 font-mono2 text-xs text-muted-foreground">
            {model.family} · {model.params}B · {(model.context / 1000).toFixed(0)}k ctx
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FavoriteButton item={catalogFavorite(model)} />
          <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground" title="Ajouter au comparateur">
            <Checkbox checked={checked} onCheckedChange={() => toggleCompare(model.id)} data-testid="compare-checkbox" />
            <Scale className="h-3.5 w-3.5" />
          </label>
        </div>
      </div>

      <p className="mt-2 text-sm text-slate-400">{model.desc}</p>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {model.use_cases.map((u) => (
          <span key={u} className={`rounded border px-1.5 py-0.5 font-mono2 text-[10px] uppercase ${USECASE_COLORS[u] || USECASE_COLORS.edge}`}>
            {u}
          </span>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2">
        <Select value={quantId} onValueChange={setQuantId}>
          <SelectTrigger data-testid="model-quantization-selector" className="h-8 w-[130px] font-mono2 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {quantizations.map((q) => (
              <SelectItem key={q.id} value={q.id} className="font-mono2 text-xs">
                {q.label} · {q.quality}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <VerdictBadge verdict={ev.verdict} data-testid="model-verdict-badge" />
      </div>

      <div className="mt-3 space-y-1.5">
        <div className="flex justify-between font-mono2 text-xs">
          <span className="text-muted-foreground">Requis ~{ev.requiredGB.toFixed(1)} Go</span>
          <span className="text-muted-foreground">Dispo {ev.target} Go</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-[#080B10]">
          <div className={`h-full rounded-full transition-all ${BAR_TONE[tone]}`} style={{ width: `${fill}%` }} />
        </div>
        <div className="flex justify-between font-mono2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><Gauge className="h-3 w-3" /> {ev.tokPerSec > 0 ? `~${ev.tokPerSec} tok/s` : "—"}</span>
          <span>{ev.mode}</span>
        </div>
      </div>

      <div className="mt-auto flex items-center gap-2 pt-4">
        <Dialog>
          <DialogTrigger asChild>
            <button data-testid="model-install-button" className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
              <Download className="h-3.5 w-3.5" /> Installer en local
            </button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto border-[#1E2638] bg-[#0F141C]">
            <DialogHeader>
              <DialogTitle className="font-head">{model.name} — Installation locale</DialogTitle>
            </DialogHeader>
            <InstallCommands model={model} />
            <Link to={`/installation?model=${model.id}`} data-testid="model-install-full-guide-link" className="text-xs text-primary hover:underline">
              Ouvrir le guide d'installation complet →
            </Link>
          </DialogContent>
        </Dialog>
        <a
          href={`https://huggingface.co/${model.hf}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 rounded-md border border-[#1E2638] px-3 py-2 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary"
        >
          <GitBranch className="h-3.5 w-3.5" /> HF
        </a>
        <Dialog>
          <DialogTrigger asChild>
            <button data-testid="model-reviews-button" title="Avis de la communauté"
              className="flex items-center gap-1 rounded-md border border-[#1E2638] px-2.5 py-2 text-xs text-muted-foreground transition-colors hover:border-amber-400 hover:text-amber-300">
              <Star className={`h-3.5 w-3.5 ${summary ? "fill-amber-400 text-amber-400" : ""}`} />
              <span data-testid="model-reviews-summary">{summary ? `${summary.avg} (${summary.count})` : "Avis"}</span>
            </button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto border-[#1E2638] bg-[#0F141C]">
            <DialogHeader>
              <DialogTitle className="font-head">{model.name} — Avis de la communauté</DialogTitle>
            </DialogHeader>
            <ModelReviews model={model} />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
